import { Box, Theme, ThemeProvider, useMediaQuery, useTheme } from '@mui/material';
import { deepmerge } from '@mui/utils';

import {
	ContentfulBackground,
	ContentfulPageThemeOptions,
	HeadingTag,
	Maybe,
	TAGContentAlignment,
	TemplateWidth,
	DisableTemplatePaddingOn,
	TRoundedCornersSize,
} from '@/types';
import {
	ServiceLineHeroBodyCopy,
	ServiceLineHeroHeadline,
	ServiceLineHeroSubHeadline,
	ServiceLineHeroButtonCollection,
	TermsAndConditions,
	FocalPointImage as TFocalPointImage,
	Video,
	VideoAsset as TVideoAsset,
	ServiceLineHeroContentListCollection,
	ImageAsset as TImageAsset,
	BffBrandType,
} from '@/types/generated';
import ServiceLineHeroContent from '@/components/ServiceLineHero/ServiceLineHeroContent/ServiceLineHeroContent';
import FocalPointImage, { FocalPointData } from '@/components/FocalPointImage';
import ImageAsset from '@/components/ImageAsset';
import { IImage } from '@/components/ImageAsset/ImageAsset';
import { usePageThemeContext } from '@/context/PageThemeProvider.ctx';
import { resolvePageThemePalette } from '@/utils/resolvePageThemePalette';
import { resolveContentfulBgColor, textToJumpLink } from '@/utils';
import VideoAsset from '@/components/VideoAsset';
import { useFacilityContext } from '@/context';
import SchedulingTimes from '@/components/SchedulingTimes/SchedulingTimes';
import SchedulingWidget from '@/components/SchedulingWidget';
import PatientInfoWidget from '@/components/PatientInfoWidget';

import TemplateContainer from '../TemplateContainer/TemplateContainer';
import { getIsScheduleTypeOpen } from '../SchedulingTimes/SchedulingTimes.helpers';

import { getContainerStyles, getMediaContainerStyles, mobileImageStyles } from './ServiceLineHero.styles';

export interface IServiceLineHero {
	/**
	 * The headline for the template.
	 *
	 * @type {Maybe<ServiceLineHeroHeadline>}
	 * @memberof IServiceLineHero
	 */
	headline?: Maybe<ServiceLineHeroHeadline>;
	/**
	 * The eyebrow Heading Tag for the template.
	 *
	 * @type {Maybe<HeadingTag>}
	 * @memberof IServiceLineHero
	 */
	eyebrowHtag?: Maybe<HeadingTag>;
	/**
	 * The eyebrow for the template.
	 *
	 * @type {Maybe<string>}
	 * @memberof IServiceLineHero
	 */
	eyebrow?: Maybe<string>;
	/**
	 * The sub headline for the template.
	 *
	 * @type {Maybe<ServiceLineHeroSubHeadline>}
	 * @memberof IServiceLineHero
	 */
	subHeadline?: Maybe<ServiceLineHeroSubHeadline>;

	/**
	 * The body copy for the template.
	 *
	 * @type {Maybe<ServiceLineHeroBodyCopy>}
	 * @memberof IServiceLineHero
	 */
	bodyCopy?: Maybe<ServiceLineHeroBodyCopy>;

	/**
	 * A collection of content children.
	 *
	 * @type {Maybe<ServiceLineHeroContentListCollection>}
	 * @memberof IServiceLineHero
	 */
	contentListCollection?: Maybe<ServiceLineHeroContentListCollection>;

	/**
	 * The collection of buttons to display. Maximum of 2 buttons.
	 *
	 * @type {Maybe<ServiceLineHeroButtonCollection>}
	 * @memberof IServiceLineHero
	 */
	buttonCollection?: Maybe<ServiceLineHeroButtonCollection>;

	/**
	 * The terms and conditions template.
	 *
	 * @type {Maybe<TermsAndConditions>}
	 * @memberof IServiceLineHero
	 */
	termsConditions?: Maybe<TermsAndConditions>;

	/**
	 * Width of the ServiceLineHero template. Can be Full or Inset.
	 *
	 * @type {Maybe<TemplateWidth>}
	 * @memberof IServiceLineHero
	 * @default 'Full'
	 */
	templateWidth?: Maybe<TemplateWidth>;

	/**
	 * Use the focal point image component to display an image with a focal point.
	 *
	 * @type {Maybe<string>}
	 * @memberof IServiceLineHero
	 * @deprecated use ImageAsset instead
	 */
	focalPointImage?: Maybe<TFocalPointImage>;

	/**
	 * Video Asset with video for Desktop and Mobile
	 *
	 * @type {Maybe<VideoAsset>}
	 * @memberof IServiceLineHero
	 */
	videoAsset?: Maybe<TVideoAsset>;

	/**
	 * An option to hide the hero image on mobile.
	 *
	 * @type {Maybe<boolean>}
	 * @memberof IServiceLineHero
	 */
	hideHeroOnMobile?: Maybe<boolean>;
	/**
	 * An option to display the hero image first and the supporting text second, or vice versa (Mobile Only).
	 *
	 * @type {Maybe<boolean>}
	 * @memberof IServiceLineHero
	 */
	imgDisplaysFirstOnMobile?: Maybe<boolean>;
	/**
	 * An option to display the hero image first and the supporting text second, or vice versa (Desktop Only).
	 *
	 * @type {Maybe<boolean>}
	 * @memberof IServiceLineHero
	 */
	imgDisplaysFirstOnDesktop?: Maybe<boolean>;
	/**
	 * An option to display a border radius on the image.
	 *
	 * @type {Maybe<boolean> | null}
	 * @memberof IServiceLineHero
	 */
	imageWithBorderRadius?: Maybe<boolean> | null;
	/**
	 * An option to set the size of rounded corner on media. (only works when imageWithBorderRadius enabled)
	 *
	 * @type {Maybe<TRoundedCornersSize>}
	 * @memberof IServiceLineHero
	 * @default 'Large'
	 */
	mediaRoundedCornerSize?: Maybe<TRoundedCornersSize>;
	/**
	 * An option to display different background colors.
	 *
	 * @type {Maybe<string>}
	 * @memberof IServiceLineHero
	 */
	backgroundColor?: Maybe<string>;
	/**
	 * An option to display different background colors for Mobile.
	 *
	 * @type {Maybe<string>}
	 * @memberof IServiceLineHero
	 */
	backgroundColorMobile?: Maybe<string>;
	/**
	 * An option to either 'Center' or 'Left' align the content
	 * @type {Maybe<TAGContentAlignment>}
	 * @memberof IServiceLineHero
	 * @default 'Left'
	 */
	contentAlign?: Maybe<TAGContentAlignment> | null;
	/**
	 * An option to disable padding on top and/or the bottom of the template.
	 *
	 * @type {Array<'Top' | 'Bottom'> | undefined}
	 * @memberof IServiceLineHero
	 * @default ['Top', 'Bottom']
	 */
	disablePaddingOn?: DisableTemplatePaddingOn;
	/**
	 * An option to display office details if any are selected.
	 *
	 * @type {Maybe<boolean> | null}
	 * @memberof IServiceLineHero
	 */
	showOfficeDetails?: Maybe<boolean> | null;
	/**
	 * Optional field for Google Map URL for business query
	 *
	 * @type {Maybe<string>}
	 * @memberof IServiceLineHero
	 */
	googleMapsBusinessQuery?: Maybe<string>;
	/**
	 * Optional field to display hours info in the modal
	 *
	 * @type {Maybe<boolean>}
	 * @memberof IServiceLineHero
	 */
	displayOfficeHoursInModal?: Maybe<boolean>;
	/**
	 * Used to identify a component on a page for the purpose of jumping to it using the anchor link.
	 *
	 * @type {Maybe<string>}
	 * @memberof IServiceLineHero
	 */
	anchorId?: Maybe<string>;
	/**
	 * Brandfolder Image with focal point.
	 *
	 * @type {Maybe<ImageAsset>}
	 * @memberof IServiceLineHero
	 */
	imageAsset?: Maybe<TImageAsset>;
	/*
	 * Image to be displayed on Scheduling sub component. Only for Lovet when scheduling is available for location.
	 *
	 * @type {Maybe<TImageAsset>}
	 * @memberof IServiceLineHero
	 */
	schedulingImageAsset?: Maybe<TImageAsset>;
	/*
	 * Enables scheduling times in place of image. Only for Lovet when scheduling is available for location.
	 *
	 * @type {Maybe<boolean>}
	 * @memberof IServiceLineHero
	 */
	allowScheduleTimes?: Maybe<boolean>;
	/**
	 * The label displayed in the ServiceLine hero office hours.
	 *
	 * @type {string}
	 * @memberof IServiceLineHero
	 */
	officeHoursLabel?: string;
	/**
	 * Enables scheduling widget in place of image. For ClearChoice to display appointment times.
	 *
	 * @type {Maybe<boolean>}
	 * @memberof IServiceLineHero
	 */
	allowScheduleWidget?: Maybe<boolean>;
	/**
	 * URL for the schedule consultation app. Used when redirecting from scheduling widget.
	 *
	 * @type {Maybe<string>}
	 * @memberof IServiceLineHero
	 */
	scheduleConsultationUrl?: Maybe<string>;
	/**
	 * Enables patient info widget in place of image. Displays a pre-consultation form that
	 * pre-populates step 2 of the scheduling flow.
	 *
	 * @type {Maybe<boolean>}
	 * @memberof IServiceLineHero
	 */
	allowPatientInfoWidget?: Maybe<boolean>;
}

export default function ServiceLineHero({
	headline,
	eyebrowHtag = 'normal',
	eyebrow,
	subHeadline,
	bodyCopy,
	termsConditions,
	templateWidth = 'Full',
	contentListCollection,
	buttonCollection,
	focalPointImage,
	videoAsset,
	hideHeroOnMobile = false,
	imgDisplaysFirstOnMobile = false,
	imgDisplaysFirstOnDesktop = false,
	imageWithBorderRadius = true,
	mediaRoundedCornerSize = 'Large',
	backgroundColor = 'White',
	backgroundColorMobile = 'White',
	contentAlign = 'Left',
	disablePaddingOn = ['Top', 'Bottom'],
	showOfficeDetails = false,
	googleMapsBusinessQuery,
	displayOfficeHoursInModal,
	anchorId,
	imageAsset,
	schedulingImageAsset,
	allowScheduleTimes = false,
	officeHoursLabel,
	allowScheduleWidget = false,
	scheduleConsultationUrl,
	allowPatientInfoWidget = false,
}: Readonly<IServiceLineHero>) {
	const theme = useTheme();
	const { palette } = theme;

	const fx = useFacilityContext(false);
	const { officeInfo, facilityBrand, facilityCode } = fx;

	if (process.env.NODE_ENV !== 'production' && allowScheduleWidget && allowPatientInfoWidget) {
		console.warn(
			'[ServiceLineHero] Both `allowScheduleWidget` and `allowPatientInfoWidget` are enabled. ' +
				'Disable `allowScheduleWidget` before enabling `allowPatientInfoWidget`. ' +
				'The scheduling widget will take precedence.'
		);
	}

	const isScheduleTypeOpen = allowScheduleTimes && getIsScheduleTypeOpen(officeInfo, facilityBrand);

	// Use 'lg' breakpoint when any widget is present for more space, otherwise use 'md'
	const hasWidget = !!(allowScheduleWidget || allowPatientInfoWidget);
	const breakpoint = hasWidget ? 'lg' : 'md';
	const isSmallScreen = useMediaQuery(theme.breakpoints.down(breakpoint));
	const pageTheme = usePageThemeContext();
	// NOTE: Fixes issue where storybook was not respecting Page Theme flag
	const componentTheme = theme.tagFeatures?.PAGE_THEME_ENABLED
		? deepmerge(theme, {
				palette: resolvePageThemePalette(pageTheme as ContentfulPageThemeOptions, palette),
		  })
		: theme;
	const backgroundToUse = isSmallScreen ? backgroundColorMobile : backgroundColor;
	// Force a light gradient when the patient info widget is shown (matches schedule-widget convention)
	let effectiveBackground: Maybe<string>;
	if (allowPatientInfoWidget) {
		effectiveBackground = 'Light Gradient';
	} else {
		effectiveBackground = backgroundToUse;
	}
	const contentfulBackgroundColor = resolveContentfulBgColor(
		effectiveBackground as ContentfulBackground,
		theme,
		componentTheme.palette.backgrounds?.default
	);

	const isDarkBackground = !!effectiveBackground?.includes('Dark');
	return (
		<ThemeProvider theme={componentTheme}>
			<TemplateContainer
				dataTestId="section_service_line_hero"
				containerSx={getContainerStyles(
					componentTheme,
					disablePaddingOn,
					!!imgDisplaysFirstOnMobile,
					!!imgDisplaysFirstOnDesktop,
					hasWidget
				)}
				contentfulBackgroundColor={contentfulBackgroundColor}
				templateWidth={(templateWidth as TemplateWidth) || 'Full'}
				{...(anchorId && { id: textToJumpLink(anchorId) })}
			>
				{/* Show widget or image in the media slot */}
				<MediaSlot
					allowScheduleWidget={allowScheduleWidget}
					allowPatientInfoWidget={allowPatientInfoWidget}
					isScheduleTypeOpen={isScheduleTypeOpen}
					imgDisplaysFirstOnDesktop={imgDisplaysFirstOnDesktop}
					scheduleConsultationUrl={scheduleConsultationUrl}
					facilityCode={facilityCode}
					videoAsset={videoAsset}
					focalPointImage={focalPointImage}
					imageAsset={imageAsset}
					imageWithBorderRadius={imageWithBorderRadius}
					isSmallScreen={isSmallScreen}
					mediaRoundedCornerSize={mediaRoundedCornerSize}
					componentTheme={componentTheme}
					hideHeroOnMobile={!!hideHeroOnMobile}
				/>
				<ServiceLineHeroContent
					key={0}
					headline={headline}
					eyebrowHtag={eyebrowHtag}
					eyebrow={eyebrow}
					subHeadline={subHeadline}
					bodyCopy={bodyCopy}
					contentListItems={contentListCollection?.items}
					buttons={hasWidget ? undefined : buttonCollection?.items}
					narrowLayout={hasWidget}
					termsConditions={termsConditions}
					parentBackgroundColor={backgroundColor}
					isDarkBackground={isDarkBackground}
					contentAlign={contentAlign}
					showOfficeDetails={showOfficeDetails}
					googleMapsBusinessQuery={googleMapsBusinessQuery}
					displayOfficeHoursInModal={displayOfficeHoursInModal}
					officeHoursLabel={officeHoursLabel}
				/>
				{isScheduleTypeOpen && facilityBrand && facilityCode && (
					<SchedulingTimes
						brand={facilityBrand as BffBrandType}
						facilityId={facilityCode}
						scheduleAppointmentURL={officeInfo?.scheduling?.schedulingUrl}
						schedulingImageAsset={schedulingImageAsset}
						roundedCornerSize={mediaRoundedCornerSize}
						componentTheme={componentTheme}
						data-testid={`service_line_hero_scheduling_times`}
					/>
				)}
			</TemplateContainer>
		</ThemeProvider>
	);
}

interface MediaSlotProps {
	allowScheduleWidget: Maybe<boolean>;
	allowPatientInfoWidget: Maybe<boolean>;
	isScheduleTypeOpen: boolean | null;
	imgDisplaysFirstOnDesktop: Maybe<boolean>;
	scheduleConsultationUrl: Maybe<string> | undefined;
	facilityCode: string | undefined;
	videoAsset?: Maybe<TVideoAsset>;
	focalPointImage?: Maybe<TFocalPointImage>;
	imageAsset?: Maybe<TImageAsset>;
	imageWithBorderRadius: Maybe<boolean>;
	isSmallScreen: boolean;
	mediaRoundedCornerSize?: Maybe<TRoundedCornersSize>;
	componentTheme: Theme;
	hideHeroOnMobile: boolean;
}

function MediaSlot({
	allowScheduleWidget,
	allowPatientInfoWidget,
	isScheduleTypeOpen,
	imgDisplaysFirstOnDesktop,
	scheduleConsultationUrl,
	facilityCode,
	...mediaProps
}: Readonly<MediaSlotProps>) {
	const widgetBoxSx = {
		padding: { xs: '1rem', md: '2rem' },
		flex: 1,
		minWidth: 0,
		width: '100%',
		maxWidth: '100%',
		boxSizing: 'border-box',
		order: imgDisplaysFirstOnDesktop ? 1 : 2,
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
	};

	if (allowScheduleWidget ?? false) {
		return (
			<Box key={1} sx={widgetBoxSx}>
				<SchedulingWidget
					enabled={true}
					facilityCode={facilityCode}
					scheduleConsultationUrl={scheduleConsultationUrl || '/schedule-a-consultation/'}
				/>
			</Box>
		);
	}

	if (allowPatientInfoWidget ?? false) {
		return (
			<Box key={1} sx={widgetBoxSx}>
				<PatientInfoWidget schedulingUrl={scheduleConsultationUrl || '/schedule-a-consultation/'} />
			</Box>
		);
	}

	if (isScheduleTypeOpen ?? false) {
		return null;
	}

	return <ServiceLineHeroMedia key={1} imgDisplaysFirstOnDesktop={imgDisplaysFirstOnDesktop} {...mediaProps} />;
}

interface ServiceLineHeroMediaProps {
	videoAsset?: Maybe<TVideoAsset>;
	focalPointImage?: Maybe<TFocalPointImage>;
	imageAsset?: Maybe<TImageAsset>;
	imageWithBorderRadius: Maybe<boolean>;
	isSmallScreen: boolean;
	imgDisplaysFirstOnDesktop?: Maybe<boolean>;
	mediaRoundedCornerSize?: Maybe<TRoundedCornersSize>;
	componentTheme: Theme;
	hideHeroOnMobile: boolean;
}

export function ServiceLineHeroMedia({
	videoAsset,
	focalPointImage,
	imageWithBorderRadius,
	isSmallScreen,
	imgDisplaysFirstOnDesktop,
	mediaRoundedCornerSize,
	componentTheme,
	hideHeroOnMobile,
	imageAsset,
}: Readonly<ServiceLineHeroMediaProps>) {
	const theme = useTheme();
	if (hideHeroOnMobile && isSmallScreen) {
		return null;
	}
	if (videoAsset) {
		return (
			<Box
				sx={getMediaContainerStyles({
					theme: componentTheme,
					mediaWithBorderRadius: imageWithBorderRadius,
					isVideo: true,
					imgDisplaysFirstOnDesktop,
					mediaRoundedCornerSize,
				})}
				key={videoAsset.sys.id}
				data-testid="service_line_hero_video_asset"
			>
				<VideoAsset
					desktopVideo={videoAsset.desktopVideo as Video}
					mobileVideo={(videoAsset.mobileVideo as Video) || undefined}
				/>
			</Box>
		);
	}

	const testId = `service_line_hero_${imageAsset ? 'image_asset' : 'focal_point_image'}`;
	if (!imageAsset && !focalPointImage) return null;
	return (
		<Box
			sx={getMediaContainerStyles({
				theme: componentTheme,
				mediaWithBorderRadius: imageWithBorderRadius,
				isVideo: false,
				imgDisplaysFirstOnDesktop,
				mediaRoundedCornerSize,
			})}
			key={focalPointImage?.sys.id}
			data-testid={testId}
		>
			{/* TODO: Remove support for FocalPointImage after content updates */}
			{imageAsset ? (
				<ImageAsset
					desktopImage={imageAsset?.desktopImage as IImage}
					mobileImage={imageAsset?.mobileImage as IImage}
					dataTestId={'img_asset_service_line_hero'}
					{...(isSmallScreen && { imageSx: mobileImageStyles })}
				/>
			) : (
				<FocalPointImage
					image={focalPointImage?.image}
					focalPoint={focalPointImage?.focalPoint as FocalPointData}
					sizes={`${theme.breakpoints.down('md').replace('@media', '')} 100vw, 900px`}
					imageSx={isSmallScreen ? mobileImageStyles : {}}
				/>
			)}
		</Box>
	);
}
